import { Injectable, OnDestroy } from '@angular/core';
import {
  HttpClient,
  HttpErrorResponse,
  HttpHeaders,
} from '@angular/common/http';

import { tap, catchError } from 'rxjs/operators';
import {
  BehaviorSubject,
  throwError,
  Subject,
  Subscription,
  Observable,
  from,
  of,
  map,
  filter,
  observable,
  take,
} from 'rxjs';

import { User } from 'src/app/models/data/user.model';
import { DropdownService } from '../dropdown/dropdown.service';
import { PageService } from '../page/page.service';
import { RouteGeneratorService } from '../route-generator/route-generator.service';
import { environment } from './../../../environments/environment';
import { LoginResponseData } from 'src/app/interfaces/LoginResponseData';
import { Router } from '@angular/router';
import { routes as staticRoutes } from '../../app.routes';

@Injectable({
  providedIn: 'root',
})
export class AuthService implements OnDestroy {
  user = new BehaviorSubject<User>({} as User);
  isPreSurveyState = new BehaviorSubject<boolean>(false);
  oldPassword = new BehaviorSubject<string>('');
  status = new Subject<number>();
  localUser!: User;
  statusSub!: Subscription;
  private userStatus: number = 0;

  constructor(
    private http: HttpClient,
    private dropService: DropdownService,
    private pageService: PageService,
    private routeGenerator: RouteGeneratorService,
    private router: Router,
  ) { }

  login(username: string, password: string) {
    console.log('🔑 AUTH SERVICE DEBUG: Starting login for user:', username);
    console.log('🔑 AUTH SERVICE DEBUG: API URL:', `${environment.apiUrl}/Login`);

    return this.http
      .put<LoginResponseData>(`${environment.apiUrl}/Login`, {
        username: username,
        password: password,
      })
      .pipe(
        catchError((error) => {
          console.error('🔑 AUTH SERVICE DEBUG: Login API error:', error);
          return this.handleError(error);
        }),
        tap((res: LoginResponseData) => {
          console.log('🔑 AUTH SERVICE DEBUG: Login API response:', res);
          localStorage.removeItem('user');

          let user = new User(
            res.username,
            res.token as string,
            res.email as string,
          );
          user.status = res.status;

          console.log('🔑 AUTH SERVICE DEBUG: User object created:', user);
          console.log('🔑 AUTH SERVICE DEBUG: User status:', res.status);

          this.user.next(user);
          localStorage.setItem('user', JSON.stringify(user));
          console.log('🔑 AUTH SERVICE DEBUG: User saved to localStorage');

          // this.autoLogin();
          if (res.status == 2) {
            console.log('🔑 AUTH SERVICE DEBUG: Setting pre-survey state to true');
            this.isPreSurveyState.next(true);
          }
        }),
      );
  }

  getUserData(): User {
    const user: {
      username: string;
      email: string;
      token: string;
      status: number;
    } = JSON.parse(localStorage.getItem('user') as string);

    if (!user) this.localUser = {} as User;
    else this.localUser = user;

    return this.localUser;
  }

  logOut() {
    this.getUserData();
    let headers = new HttpHeaders({
      token: this.localUser.token,
      'Content-Type': 'application/json',
    });

    return this.http
      .put(
        `${environment.apiUrl}/logout`,
        {},
        {
          headers: headers,
        },
      )
      .pipe(
        catchError(this.handleLogoutError),
        tap(() => {
          this.logoutAction();
        }),
      );
  }

  checkStatus(): Observable<LoginResponseData> {
    this.getUserData();
    let token: string = '';
    let status: number = 0;
    if (this.localUser) {
      if (this.localUser.token) token = this.localUser.token;
      if (this.localUser.status) status = this.localUser.status;
    }
    let headers = new HttpHeaders({
      token: token,
      'Content-Type': 'application/json',
    });

    return this.http
      .get<LoginResponseData>(`${environment.apiUrl}/loginstatus`, {
        headers: headers,
      })
      .pipe(
        tap(
          (response: LoginResponseData) => {
            if (token) {
              this.status.next(response.status);
              if (response.status == 0) {
                if (this.userStatus !== 0) {
                  this.userStatus = 0;
                }
              }

              let validLoginStatuses: number[] = [1, 2];

              if (!validLoginStatuses.includes(response.status)) {
                this.logoutAction();
              }
            }
          },
          (error) => {
            this.logoutAction();
          },
        ),
      );
  }

  changePassword(oldPassword: any, newPassword: any) {
    const passwords = {
      OldPassword: oldPassword,
      NewPassword: newPassword,
    };

    this.getUserData();

    let headers = new HttpHeaders({
      token: this.localUser.token,
      'Content-Type': 'application/json',
    });

    return this.http
      .put(`${environment.apiUrl}/login/Password/Change`, passwords, {
        headers: headers,
      })
      .pipe(
        catchError(this.handleError),
        tap((res) => {
          if (res == null) {
            console.log('🔑 PASSWORD CHANGE DEBUG: Password change successful');
            // Check if user was in pre-survey state BEFORE clearing it
            const wasPreSurvey = this.isPreSurveyState.value;
            // Check current route to determine if we should redirect
            const currentRoute = this.router.url;
            const isOnRequirePasswordChangeRoute = currentRoute.includes('requirepasswordchange');
            
            console.log('🔑 PASSWORD CHANGE DEBUG: wasPreSurvey =', wasPreSurvey);
            console.log('🔑 PASSWORD CHANGE DEBUG: currentRoute =', currentRoute);
            console.log('🔑 PASSWORD CHANGE DEBUG: isOnRequirePasswordChangeRoute =', isOnRequirePasswordChangeRoute);
            
            this.isPreSurveyState.next(false);
            // Refresh user status from API after successful password change
            this.checkStatus().subscribe((statusResponse: LoginResponseData) => {
              console.log('🔑 PASSWORD CHANGE DEBUG: Status check response:', statusResponse);
              // Update local user object with new status
              const updatedUser = this.getUserData();
              updatedUser.status = statusResponse.status;
              this.user.next(updatedUser);
              localStorage.setItem('user', JSON.stringify(updatedUser));
              console.log('🔑 PASSWORD CHANGE DEBUG: Updated user status to', statusResponse.status);

              // If user was in pre-survey state AND on the requirepasswordchange route,
              // redirect to first survey page after routes are regenerated
              // Otherwise, just regenerate routes (user can navigate manually via navbar)
              if (wasPreSurvey && isOnRequirePasswordChangeRoute) {
                console.log('🔑 PASSWORD CHANGE DEBUG: User was in pre-survey state AND on requirepasswordchange route');
                console.log('🔑 PASSWORD CHANGE DEBUG: Calling regenerateRoutesAndRedirect()');
                // User was required to change password at login and is on the password change page, redirect to first survey page
                this.regenerateRoutesAndRedirect();
              } else {
                console.log('🔑 PASSWORD CHANGE DEBUG: User changed password from profile or other page');
                console.log('🔑 PASSWORD CHANGE DEBUG: Calling regenerateRoutes() to update routes and navbar');
                // User changed password from profile or other page, just regenerate routes and update navbar
                // This ensures the survey link in navbar works correctly
                this.regenerateRoutes();
              }
            });
          }
        }),
      );
  }

  changePasswordWithRecoveryToken(recoveryToken: any, newPassword: any) {
    const passwords = {
      RecoveryToken: recoveryToken,
      NewPassword: newPassword,
    };

    let headers = new HttpHeaders({
      'Content-Type': 'application/json',
    });

    return this.http
      .put(`${environment.apiUrl}/login/Password/RecoveryChange`, passwords, {
        headers: headers,
      })
      .pipe(
        catchError(this.handleError),
        tap((res) => {
          if (res == null) {
            this.isPreSurveyState.next(false);
            // Refresh user status from API after successful password change
            this.checkStatus().subscribe((statusResponse: LoginResponseData) => {
              // Update local user object with new status
              const updatedUser = this.getUserData();
              updatedUser.status = statusResponse.status;
              this.user.next(updatedUser);
              localStorage.setItem('user', JSON.stringify(updatedUser));

              // Regenerate routes and redirect to first survey page
              // This ensures routes are ready before navigation
              this.regenerateRoutesAndRedirect();
            });
          }
        }),
      );
  }

  changeEmail(newEmail: any) {
    const body = {
      email: newEmail,
    };
    this.getUserData();
    let headers = new HttpHeaders({
      token: this.localUser.token,
      'Content-Type': 'application/json',
    });

    return this.http
      .put(`${environment.apiUrl}/login/email/Change`, body, {
        headers: headers,
      })
      .pipe(
        catchError(this.handleError),
        tap((res) => {
          if (res == null) {
            this.localUser.email = newEmail;
            localStorage.setItem('user', JSON.stringify(this.localUser));
            this.user.next(this.localUser);
            this.getUserData();
          }
        }),
      );
  }

  requestPasswordRecovery(email: any) {
    const body = {
      email: email,
    };

    let headers = new HttpHeaders({
      'Content-Type': 'application/json',
    });

    return this.http
      .put(`${environment.apiUrl}/login/password/recover`, body, {
        headers: headers,
      })
      .pipe(
        catchError(this.handleError),
        tap((res) => {
          if (res == null) {
          }
        }),
      );
  }

  autoLogin() {
    this.getUserData();
    if (!this.localUser) {
      return;
    }
    const loadedUser = new User(
      this.localUser.username,
      this.localUser.token,
      this.localUser.email as string,
    );

    this.user.next(loadedUser);
    this.statusSub = this.status.subscribe((status) => {
      if (status == 2) {
        this.isPreSurveyState.next(true);
      }
    });

    this.checkStatus();
  }

  getStatus() {
    return this.status.asObservable();
  }

  /**
   * Redirects authenticated users to the appropriate page
   * Waits for page data if necessary, then redirects to first survey page
   */
  redirectAuthenticatedUser(): void {
    console.log('🔑 AUTH SERVICE DEBUG: Starting redirect for authenticated user');

    // Check if pages are already available
    this.pageService.pages.pipe(take(1)).subscribe(pages => {
      if (pages && pages.length > 0) {
        console.log('🔑 AUTH SERVICE DEBUG: Pages already available, redirecting to first page');
        this.redirectToFirstPage(pages);
      } else {
        console.log('🔑 AUTH SERVICE DEBUG: No pages available, fetching from API');
        this.fetchPagesAndRedirect();
      }
    });
  }

  private fetchPagesAndRedirect(): void {
    console.log('🔑 AUTH SERVICE DEBUG: Fetching pages for redirect');

    this.pageService.fetchPages().pipe(take(1)).subscribe({
      next: (pages) => {
        console.log('🔑 AUTH SERVICE DEBUG: Pages fetched for redirect:', pages);

        if (pages && pages.length > 0) {
          console.log('🔑 AUTH SERVICE DEBUG: Redirecting to first page after fetch');
          this.redirectToFirstPage(pages);
        } else {
          console.log('🔑 AUTH SERVICE DEBUG: No pages available, redirecting to home');
          this.router.navigate(['/home']);
        }
      },
      error: (error) => {
        console.error('🔑 AUTH SERVICE DEBUG: Error fetching pages for redirect:', error);
        console.log('🔑 AUTH SERVICE DEBUG: Redirecting to home due to error');
        this.router.navigate(['/home']);
      }
    });
  }

  private redirectToFirstPage(pages: any[]): void {
    console.log('🔑 AUTH SERVICE DEBUG: Preparing redirect to first page');

    // Get the first page and its route
    const firstPage = pages[0];
    const route = firstPage.Route.startsWith('/') ? firstPage.Route.substring(1) : firstPage.Route;
    console.log('🔑 AUTH SERVICE DEBUG: First page details:', firstPage);
    console.log('🔑 AUTH SERVICE DEBUG: Target route:', route);

    // Check if the route already exists in the current router config
    const existingRoute = this.router.config.find(r => r.path === route);
    console.log('🔑 AUTH SERVICE DEBUG: Existing route found:', existingRoute);

    if (existingRoute) {
      // Route already exists, just navigate to it
      console.log('🔑 AUTH SERVICE DEBUG: Route already exists, navigating directly');
      this.navigateToRoute(route);
    } else {
      // Route doesn't exist, need to regenerate routes
      console.log('🔑 AUTH SERVICE DEBUG: Route not found, regenerating routes');
      this.regenerateRoutesAndNavigate(route);
    }
  }

  private navigateToRoute(route: string): void {
    console.log('🔑 AUTH SERVICE DEBUG: Attempting navigation to:', route);
    console.log('🔑 AUTH SERVICE DEBUG: Current URL before navigation:', this.router.url);

    // Try navigateByUrl first
    this.router.navigateByUrl(`/${route}`).then(result => {
      console.log('🔑 AUTH SERVICE DEBUG: Navigation result:', result);
      console.log('🔑 AUTH SERVICE DEBUG: Current URL after navigation:', this.router.url);

      if (!result) {
        console.log('🔑 AUTH SERVICE DEBUG: Navigation failed, trying fallback');
        this.router.navigate([route]).then(fallbackResult => {
          console.log('🔑 AUTH SERVICE DEBUG: Fallback navigation result:', fallbackResult);
          if (!fallbackResult) {
            console.log('🔑 AUTH SERVICE DEBUG: All navigation attempts failed, redirecting to home');
            this.router.navigate(['/home']);
          }
        });
      }
    }).catch(error => {
      console.error('🔑 AUTH SERVICE DEBUG: Navigation error:', error);
      this.router.navigate([route]).then(fallbackResult => {
        console.log('🔑 AUTH SERVICE DEBUG: Fallback navigation result:', fallbackResult);
        if (!fallbackResult) {
          console.log('🔑 AUTH SERVICE DEBUG: Fallback navigation also failed, redirecting to home');
          this.router.navigate(['/home']);
        }
      });
    });
  }

  private regenerateRoutesAndNavigate(route: string): void {
    console.log('🔑 AUTH SERVICE DEBUG: Regenerating routes for:', route);

    // Use original static routes from app.routes.ts, not the current router.config
    // This ensures we always have the base static routes to work with
    console.log('🔑 AUTH SERVICE DEBUG: Using original static routes from app.routes.ts');

    // Generate dynamic routes, passing original static routes to avoid conflicts
    // Get pages from the service - use the pages that were passed to redirectToFirstPage
    this.pageService.pages.pipe(take(1)).subscribe(pages => {
      const dynamicRoutes = this.routeGenerator.generateRoutesFromPages(pages, staticRoutes);
      console.log('🔑 AUTH SERVICE DEBUG: Dynamic routes generated for redirect:', dynamicRoutes);

      // Now filter static routes for final route combination (exclude ONLY dynamic routes that were created)
      // Keep ALL static routes including custom component routes
      // Exclude the wildcard route (**) as we'll add it at the end
      const filteredStaticRoutes = staticRoutes.filter(r =>
        r.path !== '**' &&
        !['fellowships', 'sharing', 'workers', 'training', 'children', 'compassion',
          'activities', 'example-stories', 'community-reach', 'additional-stories',
          'discipleship', 'new-fellowships', 'outreach-persecuted', 'outreach-refugees',
          'outreach-children', 'outreach-ethnic-groups', 'meeting-needs', 'additional-outreach',
          'cam-help', 'obstacles', 'other-agencies', 'partnerships', 'prayer-requests'].includes(r.path || '')
      );
      console.log('🔑 AUTH SERVICE DEBUG: Static routes filtered for redirect:', filteredStaticRoutes);

      // Combine routes with DYNAMIC ROUTES FIRST to ensure they take precedence
      // Include wildcard route at the end for invalid route handling
      const allRoutes = [...dynamicRoutes, ...filteredStaticRoutes, {
        path: '**',
        redirectTo: 'home'
      }];
      console.log('🔑 AUTH SERVICE DEBUG: All routes combined for redirect (with wildcard):', allRoutes);

      // Update router configuration
      this.router.resetConfig(allRoutes);
      console.log('🔑 AUTH SERVICE DEBUG: Router configuration updated for redirect');
      console.log('🔑 AUTH SERVICE DEBUG: Current router config after update:', this.router.config);

      // Debug: Check if target route exists in the config
      const targetRoute = this.router.config.find(r => r.path === route);
      console.log('🔑 AUTH SERVICE DEBUG: Target route found:', targetRoute);

      // Debug: List all route paths
      const allPaths = this.router.config.map(r => r.path).filter(path => path);
      console.log('🔑 AUTH SERVICE DEBUG: All route paths:', allPaths);

      // Add a small delay to ensure router config is updated
      setTimeout(() => {
        this.navigateToRoute(route);
      }, 100);
    });
  }


  private regenerateRoutes(): void {
    console.log('🔑 REGENERATE ROUTES DEBUG: Starting route regeneration');
    this.pageService.fetchPages().subscribe(pages => {
      console.log('🔑 REGENERATE ROUTES DEBUG: Pages fetched:', pages?.length || 0, 'pages');
      if (pages && pages.length > 0) {
        console.log('🔑 REGENERATE ROUTES DEBUG: First page route:', pages[0]?.Route);
        // Update the Survey link in navbar
        this.dropService.updateSurveyRoute(pages);
        console.log('🔑 REGENERATE ROUTES DEBUG: Updated survey route in navbar');

        // Use original static routes from app.routes.ts, not the current router.config
        // This ensures we always have the base static routes to work with
        console.log('🔑 REGENERATE ROUTES DEBUG: Using original static routes from app.routes.ts');
        console.log('🔑 REGENERATE ROUTES DEBUG: Original static routes count:', staticRoutes.length);

        // Generate dynamic routes, passing original static routes to avoid conflicts
        const dynamicRoutes = this.routeGenerator.generateRoutesFromPages(pages, staticRoutes);
        console.log('🔑 REGENERATE ROUTES DEBUG: Generated dynamic routes count:', dynamicRoutes.length);
        console.log('🔑 REGENERATE ROUTES DEBUG: Dynamic route paths:', dynamicRoutes.map(r => r.path));

        // Filter static routes for final combination (exclude ONLY dynamic routes that were created)
        // Keep ALL static routes including custom component routes
        // Exclude the wildcard route (**) as we'll add it at the end
        const filteredStaticRoutes = staticRoutes.filter(r =>
          r.path !== '**' &&
          !['fellowships', 'sharing', 'workers', 'training', 'children', 'compassion',
            'activities', 'example-stories', 'community-reach', 'additional-stories',
            'discipleship', 'new-fellowships', 'outreach-persecuted', 'outreach-refugees',
            'outreach-children', 'outreach-ethnic-groups', 'meeting-needs', 'additional-outreach',
            'cam-help', 'obstacles', 'other-agencies', 'partnerships', 'prayer-requests'].includes(r.path || '')
        );
        console.log('🔑 REGENERATE ROUTES DEBUG: Filtered static routes count:', filteredStaticRoutes.length);

        // Include wildcard route at the end for invalid route handling
        const allRoutes = [...dynamicRoutes, ...filteredStaticRoutes, {
          path: '**',
          redirectTo: 'home'
        }];
        console.log('🔑 REGENERATE ROUTES DEBUG: Total routes count:', allRoutes.length);
        console.log('🔑 REGENERATE ROUTES DEBUG: All route paths:', allRoutes.map(r => r.path || r.redirectTo));
        this.router.resetConfig(allRoutes);
        console.log('🔑 REGENERATE ROUTES DEBUG: Router config reset complete');
      } else {
        console.log('🔑 REGENERATE ROUTES DEBUG: No pages available, routes not regenerated');
      }
    });
  }

  /**
   * Regenerates routes and redirects to the first survey page
   * Used after password reset to ensure routes are ready before navigation
   */
  private regenerateRoutesAndRedirect(): void {
    console.log('🔑 AUTH SERVICE DEBUG: Regenerating routes and redirecting after password reset');
    
    this.pageService.fetchPages().pipe(take(1)).subscribe(pages => {
      if (pages && pages.length > 0) {
        // Update the Survey link in navbar
        this.dropService.updateSurveyRoute(pages);

        // Use original static routes from app.routes.ts, not the current router.config
        // This ensures we always have the base static routes to work with
        console.log('🔑 AUTH SERVICE DEBUG: Using original static routes from app.routes.ts');

        // Generate dynamic routes, passing original static routes to avoid conflicts
        const dynamicRoutes = this.routeGenerator.generateRoutesFromPages(pages, staticRoutes);

        // Filter static routes for final combination (exclude ONLY dynamic routes that were created)
        // Keep ALL static routes including custom component routes
        // Exclude the wildcard route (**) as we'll add it at the end
        const filteredStaticRoutes = staticRoutes.filter(r =>
          r.path !== '**' &&
          !['fellowships', 'sharing', 'workers', 'training', 'children', 'compassion',
            'activities', 'example-stories', 'community-reach', 'additional-stories',
            'discipleship', 'new-fellowships', 'outreach-persecuted', 'outreach-refugees',
            'outreach-children', 'outreach-ethnic-groups', 'meeting-needs', 'additional-outreach',
            'cam-help', 'obstacles', 'other-agencies', 'partnerships', 'prayer-requests'].includes(r.path || '')
        );

        // Include wildcard route at the end for invalid route handling
        const allRoutes = [...dynamicRoutes, ...filteredStaticRoutes, {
          path: '**',
          redirectTo: 'home'
        }];
        this.router.resetConfig(allRoutes);

        // After routes are regenerated, redirect to first survey page
        console.log('🔑 AUTH SERVICE DEBUG: Routes regenerated, redirecting to first page');
        this.redirectToFirstPage(pages);
      } else {
        console.log('🔑 AUTH SERVICE DEBUG: No pages available, redirecting to home');
        this.router.navigate(['/home']);
      }
    });
  }

  private handleError(errorRes: HttpErrorResponse): Observable<never> {
    let errorMessage = 'An unknown error occurred!';

    // Check if there's an error response with details
    if (errorRes.error && errorRes.error.error) {
      errorMessage = errorRes.error.error;
    } else if (errorRes.error && errorRes.error.message) {
      errorMessage = errorRes.error.message;
    } else if (errorRes.message) {
      errorMessage = errorRes.message;
    }

    console.error('🔑 AUTH SERVICE DEBUG: Detailed error:', {
      status: errorRes.status,
      statusText: errorRes.statusText,
      error: errorRes.error,
      message: errorMessage
    });

    return throwError(() => new Error(errorMessage));
  }

  private handleLogoutError(errorRes: HttpErrorResponse): Observable<never> {
    this.logoutAction();
    return this.handleError(errorRes);
  }

  private logoutAction(): void {
    if (!this.localUser.username) return;

    localStorage.removeItem('user');
    // Clear photo upload completion status on logout
    localStorage.removeItem('photoUploadCompleted');
    this.user.next({} as User);

    this.router.navigate(['/']);
    this.dropService.closeSidebar();
    this.userStatus = 0;
    this.status.next(0);
  }

  ngOnDestroy() {
    this.statusSub.unsubscribe();
  }
}
